import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import API from '../../../services/API'
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import FeedbackIcon from '@mui/icons-material/Feedback'
import Loading from '../../loading/loadingPage'
import AmbuNetLogo from '../../../assets/images/ambunetLogo.png'
import { TenantSettingsContext } from '../../../App'

const IDCheck = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [employee, setEmployee] = useState({})
  const [notFound, setNotFound] = useState(false)
  const [fetchError, setFetchError] = useState(false)

  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  useEffect(() => {
    API.get(`/id-check/${id}`)
      .then((res) => {
        if (res.status === 200) {
          setEmployee(res.data)
        } else if (res.status === 404) {
          setNotFound(true)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [id])

  const isEmployed = ['New Starter', 'Live'].includes(employee.status)

  const fullName = `${employee.userAccount?.firstName ?? ''} ${
    employee.userAccount?.lastName ?? ''
  }`.trim()

  const orgLogo =
    orgSettings?.logo ||
    orgSettings?.orgLogo ||
    orgSettings?.branding?.logo ||
    null

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems={{ xs: 'flex-start', lg: 'center' }}
      justifyContent="center"
      padding={2}
      sx={{
        background:
          'radial-gradient(circle at top left, rgba(35,154,164,0.25), transparent 35%), linear-gradient(135deg, #1A2230 0%, #242F40 45%, #101827 100%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 720,
          borderRadius: 4,
          overflow: 'hidden',
          background: 'rgba(255,255,255,0.96)',
          border: '1px solid rgba(255,255,255,0.16)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
        }}
      >
        <Box
          sx={{
            background:
              'linear-gradient(135deg, #242F40 0%, #2c3e50 60%, #239AA4 160%)',
            color: 'white',
            padding: 3,
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Stack spacing={0.5}>
              <Typography
                variant="overline"
                sx={{ opacity: 0.75 }}
                textAlign={{ xs: 'center', lg: 'inherit' }}
              >
                Staff ID Verification
              </Typography>
              <Typography variant="h5" fontWeight={700} color="white">
                {orgSettings?.orgName || 'Organisation'}
              </Typography>
            </Stack>

            <Box
              component="img"
              src={orgLogo || AmbuNetLogo}
              alt="Organisation logo"
              sx={{
                maxHeight: 58,
                maxWidth: 150,
                objectFit: 'contain',
                backgroundColor: 'white',
                borderRadius: 2,
                padding: 1,
              }}
            />
          </Stack>
        </Box>

        <Box padding={3}>
          {loading ? (
            <Loading />
          ) : fetchError ? (
            <Alert severity="error">
              Unable to verify this ID at the moment. Please try again later.
            </Alert>
          ) : notFound ? (
            <Alert severity="error">
              <AlertTitle>Employee not found</AlertTitle>
              This ID could not be verified.
            </Alert>
          ) : (
            <Stack spacing={3}>
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" justifyContent="center">
                      <Avatar
                        src={employee.userAccount?.photograph}
                        alt={fullName}
                        sx={{
                          width: 170,
                          height: 170,
                          borderRadius: 3,
                          border: '4px solid #E2E8F0',
                          boxShadow: '0 12px 32px rgba(36,47,64,0.18)',
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={8}>
                    <Stack spacing={1.5}>
                      <Stack
                        direction={{ xs: 'columnn', lg: 'row' }}
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Typography
                          variant="h4"
                          fontWeight={800}
                          // color="#242F40"
                          color="contrast.dark"
                        >
                          {fullName}
                        </Typography>

                        {employee.personnelInfo?.pronouns && (
                          <Chip
                            label={employee.personnelInfo.pronouns}
                            size="small"
                            sx={{
                              backgroundColor: '#F1F5F9',
                              color: '#242F40',
                              fontStyle: 'italic',
                            }}
                          />
                        )}
                      </Stack>

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {employee.clinicalRole && (
                          <Chip
                            label={employee.clinicalRole}
                            sx={{
                              backgroundColor: '#239AA4',
                              color: 'white',
                              fontWeight: 600,
                              width: '100%',
                            }}
                          />
                        )}

                        {employee.jobTitle && (
                          <Chip
                            label={employee.jobTitle}
                            sx={{
                              backgroundColor: '#242F40',
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Stack>

                      {employee.registrationNumber?.number && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>
                            {employee.registrationNumber.body?.toUpperCase()}:
                          </strong>{' '}
                          {employee.registrationNumber.number}
                        </Typography>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {isEmployed ? (
                <Alert
                  icon={<VerifiedUserIcon />}
                  severity="success"
                  sx={{
                    borderRadius: 3,
                    alignItems: 'center',
                    '& .MuiAlert-message': { width: '100%' },
                  }}
                >
                  <AlertTitle>Actively employed</AlertTitle>
                  This person is currently verified as a member of staff for{' '}
                  <strong>{orgSettings?.orgName || 'this organisation'}</strong>
                  .
                </Alert>
              ) : (
                <Alert
                  icon={<WarningAmberIcon />}
                  severity="error"
                  sx={{ borderRadius: 3 }}
                >
                  <AlertTitle>Not actively employed</AlertTitle>
                  This person is not currently verified as active. Please
                  contact{' '}
                  <strong>
                    {orgSettings?.orgName || 'the organisation'}
                  </strong>{' '}
                  before relying on this ID.
                </Alert>
              )}

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={2}
                gap={2}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  order={{ xs: 2, lg: 1 }}
                  textAlign="center"
                >
                  Verification provided securely by{' '}
                  <Link href="https://ambunet.co.uk">AmbuNet</Link>.
                </Typography>

                <Button
                  order={{ xs: 1, lg: 2 }}
                  variant="contained"
                  startIcon={<FeedbackIcon />}
                  onClick={() => navigate('/patient-feedback')}
                  sx={{
                    borderRadius: 999,
                    backgroundColor: '#239AA4',
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    '&:hover': {
                      backgroundColor: '#1E7F88',
                    },
                  }}
                >
                  Leave feedback
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default IDCheck
